import React, { useState } from "react";
import { Box, Typography, IconButton, Tooltip, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const ProductCard = ({
  product,
  isFournisseur,
  visiblePurchasePrices,
  onToggleVisibility,
  onSelect,
  onUpdatePrices,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrices, setEditedPrices] = useState({
    prix_achat: product.prix_achat,
    prix_vente: product.prix_vente,
  });

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    await onUpdatePrices(product.id_produit, editedPrices);
    setIsEditing(false);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setEditedPrices({
      prix_achat: product.prix_achat,
      prix_vente: product.prix_vente,
    });
    setIsEditing(false);
  };

  const handlePriceChange = (field) => (e) => {
    const value = parseFloat(e.target.value) || 0;
    setEditedPrices((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Box
      sx={{
        p: 2.5,
        cursor: "pointer",
        transition: "all 0.3s ease",
        borderRadius: 2,
        border: "1px solid",
        borderColor:
          product.stock_actuel <= 0
            ? "error.main"
            : product.stock_actuel <= product.stock_minimum
              ? "warning.main"
              : "divider",
        bgcolor:
          product.stock_actuel <= 0
            ? "error.lighter"
            : product.stock_actuel <= product.stock_minimum
              ? "warning.lighter"
              : "background.paper",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (theme) => theme.shadows[4],
          bgcolor:
            product.stock_actuel <= 0
              ? "error.light"
              : product.stock_actuel <= product.stock_minimum
                ? "warning.light"
                : "action.hover",
        },
      }}
      onClick={() => !isEditing && onSelect(product)}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" component="div" fontWeight={600}>
            {product.designation}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ref: {product.reference}
          </Typography>
          {product.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {product.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 200, textAlign: "right" }}>
          {isEditing ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                size="small"
                label="Prix d'achat"
                type="number"
                value={editedPrices.prix_achat}
                onChange={handlePriceChange("prix_achat")}
                onClick={(e) => e.stopPropagation()}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
              <TextField
                size="small"
                label="Prix de vente"
                type="number"
                value={editedPrices.prix_vente}
                onChange={handlePriceChange("prix_vente")}
                onClick={(e) => e.stopPropagation()}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <IconButton size="small" onClick={handleSave} color="success">
                  <SaveIcon />
                </IconButton>
                <IconButton size="small" onClick={handleCancel} color="error">
                  <CancelIcon />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Typography
                  variant="subtitle1"
                  component="div"
                  fontWeight={600}
                  color="primary.main"
                >
                  {new Intl.NumberFormat("fr-FR", {
                    style: "currency",
                    currency: "MAD",
                  }).format(
                    isFournisseur ? product.prix_achat : product.prix_vente
                  )}
                </Typography>
                {isFournisseur ? (
                  <Tooltip title="Modifier les prix">
                    <IconButton size="small" onClick={handleEditClick}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={
                      visiblePurchasePrices.has(product.id_produit)
                        ? "Masquer prix d'achat"
                        : "Afficher prix d'achat"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleVisibility(product.id_produit);
                      }}
                    >
                      {visiblePurchasePrices.has(product.id_produit) ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {visiblePurchasePrices.has(product.id_produit) &&
                !isFournisseur && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Prix d'achat:{" "}
                    {new Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "MAD",
                    }).format(product.prix_achat)}
                  </Typography>
                )}
              <Typography
                variant="body2"
                sx={{
                  color:
                    product.stock_actuel <= 0
                      ? "error.main"
                      : product.stock_actuel <= product.stock_minimum
                        ? "warning.main"
                        : "success.main",
                  fontWeight: 500,
                  mt: 0.5,
                }}
              >
                Stock: {product.stock_actuel}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductCard;
